/**
 * OrderingContext — single source of truth for the menu ordering experience.
 * Bundles the cart, Square name resolution, role gate, and UI panel state so
 * any menu component can `useOrdering()` without prop-drilling.
 *
 * The gate follows the backend config (GET /api/square-order): ordering is
 * public only when the backend is in production AND the ONLINE_ORDERING_PUBLIC
 * switch (botConfig/main) is 'true'. Otherwise managers/admins/owners only —
 * both for sandbox testing and for a staff-only soft launch in production.
 *
 * `kiosk` is the shared ordering tablet at the truck: nothing about a customer
 * is kept on the device (no order history, no remembered contact, a cart that
 * lives only in the tab) and `resetSession()` wipes the screen for the next one.
 */
import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { MANAGE_ROLES } from '../../../utils/roles';
import { useCart } from './useCart';
import { useSquareNameMap } from './squareNames';
import { getPaymentConfig } from './orderApi';
import { loadHistory, saveOrderToHistory, updateOrderInHistory, clearHistory } from './orderHistory';

const NO_HISTORY = [];

const OrderingContext = createContext({
  canOrder: false,
  gateLoading: true,
  payEnv: null,
  cart: null,
  nameMap: null,
  openCart: () => {},
});

export function OrderingProvider({ role, kiosk = false, children }) {
  const isManager = MANAGE_ROLES.includes(role);
  // Staff tools (86, order management, name mapping) never show on the kiosk,
  // even when a manager signed the tablet in to run it before public launch.
  const isStaff = isManager && !kiosk;
  // null = still resolving; staff never wait on it.
  const [payEnv, setPayEnv] = useState(null);
  const [publicOrdering, setPublicOrdering] = useState(false);
  useEffect(() => {
    let alive = true;
    getPaymentConfig()
      .then(cfg => {
        if (!alive) return;
        setPayEnv(cfg.environment === 'production' ? 'production' : 'sandbox');
        setPublicOrdering(cfg.publicOrdering === true);
      })
      .catch(() => { if (alive) setPayEnv('sandbox'); });
    return () => { alive = false; };
  }, []);
  const canOrder = (payEnv === 'production' && publicOrdering) || isManager;
  const gateLoading = payEnv === null && !isManager;
  const cart = useCart({ kiosk });
  // Only the cart and the staff mapping editor resolve names — don't load them
  // for a customer who is just browsing the menu.
  const nameMap = useSquareNameMap(isStaff || canOrder);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [ordersOpen, setOrdersOpen] = useState(false); // staff order-management panel
  // The Square item currently open in the configure-and-add modal (null = closed).
  const [activeItem, setActiveItem] = useState(null);
  // The order screen docks the cart beside the menu on wide screens; "open the
  // cart" then has nothing to open.
  const [cartDocked, setCartDocked] = useState(false);
  // A payment is in flight — the kiosk must not reset underneath it.
  const [paying, setPaying] = useState(false);
  // Bumped on every kiosk reset so the screen remounts clean (search, scroll).
  const [sessionId, setSessionId] = useState(0);

  // Short-lived confirmation ("Added Masala Dosa").
  const [notice, setNotice] = useState('');
  const noticeTimer = useRef(null);
  const notify = useCallback((msg) => {
    setNotice(msg);
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(''), 3200);
  }, []);
  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  // Device-local order history + the live menu (shared by OrderScreen so the
  // drawer/history modal can suggest and reorder against current items).
  const [history, setHistory] = useState(() => (kiosk ? NO_HISTORY : loadHistory()));
  const [menuCategories, setMenuCategories] = useState([]);
  const recordOrder = useCallback((order) => { if (!kiosk) setHistory(saveOrderToHistory(order)); }, [kiosk]);
  const updateOrder = useCallback((orderId, patch) => { if (!kiosk) setHistory(updateOrderInHistory(orderId, patch)); }, [kiosk]);
  const wipeHistory = useCallback(() => { clearHistory(); setHistory([]); }, []);

  const clearCart = cart.clear;
  const resetSession = useCallback(() => {
    clearCart();
    setDrawerOpen(false); setCheckoutOpen(false); setHistoryOpen(false);
    setMappingOpen(false); setOrdersOpen(false); setActiveItem(null);
    setNotice('');
    setSessionId(n => n + 1);
    window.scrollTo(0, 0);
  }, [clearCart]);

  const value = useMemo(() => ({
    canOrder,
    isStaff,
    kiosk,
    gateLoading,
    payEnv,
    cart,
    nameMap,
    drawerOpen, setDrawerOpen,
    checkoutOpen, setCheckoutOpen,
    mappingOpen, setMappingOpen,
    historyOpen, setHistoryOpen,
    ordersOpen, setOrdersOpen,
    history, recordOrder, updateOrder, wipeHistory,
    menuCategories, setMenuCategories,
    activeItem, setActiveItem,
    cartDocked, setCartDocked,
    paying, setPaying,
    sessionId, resetSession,
    notice, notify,
    openItem: (item) => setActiveItem(item),
    closeItem: () => setActiveItem(null),
    openCart: () => { if (!cartDocked) setDrawerOpen(true); },
    addToCart: (item, qty) => { cart.add(item, qty); setDrawerOpen(true); },
    // Rich add from the configurator: { catalogObjectId, variationId, ... }
    addConfigured: (cfg, qty) => { cart.addConfigured(cfg, qty); },
  }), [canOrder, isStaff, kiosk, gateLoading, payEnv, cart, nameMap, drawerOpen, checkoutOpen, mappingOpen,
       historyOpen, ordersOpen, history, recordOrder, updateOrder, wipeHistory, menuCategories, activeItem,
       cartDocked, paying, sessionId, resetSession, notice, notify]);

  return <OrderingContext.Provider value={value}>{children}</OrderingContext.Provider>;
}

export function useOrdering() {
  return useContext(OrderingContext);
}
